// components/WelcomeGuide.jsx — visite guidée dynamique (spotlight).
//
// Contrairement à une simple modale de slides statiques, ce composant
// navigue réellement vers la page concernée et met en surbrillance le vrai
// élément de menu correspondant (via un attribut data-tour posé sur les
// NavLink dans Layout.jsx) — l'utilisateur voit la vraie section derrière le
// halo, et peut même cliquer dessus directement pendant la visite.
//
// Technique du "trou de lumière" : un petit encart positionné exactement
// sur l'élément ciblé, avec un box-shadow énorme (0 0 0 9999px) qui assombrit
// tout l'écran SAUF cette zone. Comme cet encart n'a pas de fond et est en
// pointer-events:none, les clics traversent jusqu'à l'élément réel en
// dessous — rien n'est bloqué, contrairement à un simple overlay de modale.
//
// Affiché automatiquement une seule fois par utilisateur à la première
// connexion (voir Layout.jsx, clé localStorage `eg_onboarding_seen_<userId>`),
// et rouvrable à tout moment via l'icône d'aide (« ? »).
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, CreditCard, Package,
  CalendarClock, ShieldCheck, ChevronLeft, ChevronRight, X, Check,
} from 'lucide-react';

// `target` = valeur de l'attribut data-tour posé sur le NavLink correspondant
// dans Layout.jsx. Les étapes sans `target` (intro/outro) s'affichent comme
// une carte centrée classique, sans mise en surbrillance.
const ALL_STEPS = [
  {
    icon: LayoutDashboard,
    color: '#7c5cfc',
    title: 'Bienvenue sur EscapeOS',
    bullets: [
      "Le menu à gauche (☰ sur mobile) est regroupé par thème : Exploitation, Finance, RH & Planning, puis Administration.",
      "Ce que vous voyez dépend de votre rôle (staff, manager, admin) et des fonctionnalités incluses dans votre offre.",
      "Suivez le halo lumineux : chaque étape met en surbrillance la vraie section du menu, que vous pouvez ouvrir directement.",
    ],
  },
  {
    icon: Calendar,
    color: '#00d2b4',
    title: 'Réservations & planning',
    perm: 'bookings.view',
    target: 'nav-reservations',
    navTo: '/app/reservations',
    bullets: [
      "Créez une réservation en quelques clics, avec calcul automatique du tarif selon le nombre de joueurs.",
      "Filtrez par salle, par date ou par statut de paiement pour retrouver une session rapidement.",
      "Timer Salles (juste au-dessus) pilote le décompte en direct pendant la partie.",
    ],
  },
  {
    icon: Users,
    color: '#7c5cfc',
    title: 'Clients',
    perm: 'customers.view',
    target: 'nav-clients',
    navTo: '/app/clients',
    bullets: [
      "Chaque client a une fiche avec l'historique complet de ses réservations et son montant total dépensé.",
      "Les doublons (même client créé deux fois) sont détectés et peuvent être fusionnés en un clic.",
    ],
  },
  {
    icon: CreditCard,
    color: '#f5a623',
    title: 'Caisse & paiements',
    perm: 'cash.view',
    target: 'nav-caisse',
    navTo: '/app/caisse',
    bullets: [
      "En fin de service, Caisse vous guide dans le comptage billets/pièces et compare au montant attendu.",
      "Tout écart est mis en évidence immédiatement, avec la possibilité d'ajouter une note explicative.",
    ],
  },
  {
    icon: Package,
    color: '#22d3a0',
    title: 'Mode caisse',
    perm: 'pos.access',
    target: 'nav-pos',
    navTo: '/app/caisse-pos',
    bullets: [
      "Le point de vente pour l'encaissement rapide, avec ses onglets internes : Stock (produits, catégories, TVA), Happy Hours et Commandes à table.",
      "Commandes à table : un QR code par table, vos clients commandent et paient depuis leur téléphone, suivi en direct (en attente, en préparation, prête, servie).",
    ],
  },
  {
    icon: CalendarClock,
    color: '#f5a623',
    title: 'RH & Planning',
    perm: 'schedule.view',
    target: 'nav-planning',
    navTo: '/app/planning',
    bullets: [
      "Construisez le planning hebdomadaire de l'équipe, par site, à partir de modèles réutilisables.",
      "Le pointage horaire (arrivée/départ) suit automatiquement le temps travaillé.",
    ],
  },
  {
    icon: ShieldCheck,
    color: '#ff8bd0',
    title: 'Administration',
    min: 'manager',
    target: 'nav-admin',
    navTo: '/app/admin/salles',
    bullets: [
      "Équipe & Accès : gérez les comptes, rôles et permissions individuelles de chaque collaborateur.",
      "Salles & Tarifs, Tarification (profils, périodes, moyens de paiement), Sites, Paramètres : toute la configuration commerciale.",
    ],
  },
  {
    icon: Check,
    color: '#22d3a0',
    title: "C'est à vous de jouer",
    bullets: [
      "L'icône d'aide (?) reste toujours accessible pour revoir cette visite quand vous voulez.",
      "Le bouton « Code support » permet d'autoriser une assistance ponctuelle sans jamais partager votre mot de passe.",
      "N'hésitez pas à explorer : les actions sensibles (suppression, annulation...) demandent toujours une confirmation.",
    ],
  },
];

const MOBILE_BREAKPOINT = 768;

// Cherche, parmi tous les éléments partageant ce data-tour (desktop caché +
// copie dans le drawer mobile coexistent dans le DOM), celui qui est
// réellement visible à l'écran.
function findVisibleTarget(id) {
  if (!id) return null;
  const els = document.querySelectorAll(`[data-tour="${id}"]`);
  for (const el of els) {
    if (el.offsetParent !== null) return el;
  }
  return null;
}

export default function WelcomeGuide({ onClose, can, openDrawer, closeDrawer }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  // Ne garder que les étapes pertinentes pour ce rôle / cette offre — un
  // manager ne voit pas la promesse d'un accès Administration qu'il n'a pas.
  const steps = useMemo(() => ALL_STEPS.filter(s => {
    if (s.perm && can && !can(s.perm)) return false;
    if (s.min && can && !can(s.min)) return false;
    return true;
  }), [can]);

  const isLast = step === steps.length - 1;
  const current = steps[step];
  const Icon = current.icon;

  // Navigue réellement vers la page concernée, ouvre le tiroir mobile si
  // besoin, puis mesure la position du vrai élément de menu à mettre en
  // surbrillance (avec quelques tentatives : le tiroir met un instant à
  // s'afficher après ouverture).
  useEffect(() => {
    if (current.navTo && current.navTo !== location.pathname) navigate(current.navTo);

    if (!current.target) {
      closeDrawer?.();
      setRect(null);
      return;
    }

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    if (isMobile) openDrawer?.(); else closeDrawer?.();

    // Repart de zéro pour cette étape : évite d'afficher brièvement le halo
    // à l'ancienne position pendant que la nouvelle cible se mesure.
    setRect(null);
    let attempts = 0;
    const measure = () => {
      const el = findVisibleTarget(current.target);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (attempts < 30) {
        attempts++;
        rafRef.current = requestAnimationFrame(measure);
      }
    };
    rafRef.current = requestAnimationFrame(measure);

    const onResize = () => {
      const el = findVisibleTarget(current.target);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [step]);

  const finish = () => {
    closeDrawer?.();
    onClose();
  };

  // Étape sans cible : carte centrée classique (intro / conclusion).
  if (!current.target) {
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && finish()}>
        <TourCard current={current} Icon={Icon} step={step} steps={steps}
          onPrev={() => setStep(s => s - 1)}
          onNext={() => isLast ? finish() : setStep(s => s + 1)}
          onSkip={finish} isLast={isLast}/>
      </div>
    );
  }

  // Étape avec cible mais pas encore mesurée (tiroir mobile en cours
  // d'ouverture, par ex.) : ne rien afficher plutôt qu'un flash de modale
  // plein écran qui disparaîtrait aussitôt.
  if (!rect) return null;

  // Étape avec cible : halo lumineux positionné sur le vrai élément de menu,
  // + carte flottante à côté (pas de fond plein écran — le reste de l'appli
  // reste cliquable en dessous).
  const pad = 6;
  const spot = {
    position: 'fixed', top: rect.top - pad, left: rect.left - pad,
    width: rect.width + pad * 2, height: rect.height + pad * 2,
    borderRadius: 10, boxShadow: '0 0 0 9999px rgba(6,7,12,0.72)',
    pointerEvents: 'none', zIndex: 300, transition: 'all 0.2s ease',
    border: '2px solid rgba(124,92,252,0.8)',
  };

  const cardWidth = 320;
  const spaceRight = window.innerWidth - (rect.left + rect.width);
  const placeRight = spaceRight > cardWidth + 40;
  const cardTop = Math.min(Math.max(rect.top - 20, 16), window.innerHeight - 340);
  const cardStyle = {
    position: 'fixed', zIndex: 301, width: cardWidth, top: cardTop,
    ...(placeRight
      ? { left: Math.min(rect.left + rect.width + 24, window.innerWidth - cardWidth - 16) }
      : { left: Math.max(rect.left - cardWidth - 24, 16) }),
  };

  return (
    <>
      <div style={spot}/>
      <div className="modal" style={cardStyle}>
        <TourCardBody current={current} Icon={Icon} step={step} steps={steps}
          onPrev={() => setStep(s => s - 1)}
          onNext={() => isLast ? finish() : setStep(s => s + 1)}
          onSkip={finish} isLast={isLast} onClose={finish}/>
      </div>
    </>
  );
}

function TourCard({ current, Icon, step, steps, onPrev, onNext, onSkip, isLast }) {
  return (
    <div className="modal" style={{ maxWidth: 460, textAlign: 'center', position: 'relative' }}>
      <button onClick={onSkip} title="Fermer"
        style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none',
          cursor: 'pointer', color: 'var(--text-3)' }}>
        <X size={18}/>
      </button>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: `${current.color}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px auto 16px' }}>
        <Icon size={26} color={current.color}/>
      </div>
      <TourCardBody current={current} Icon={Icon} step={step} steps={steps}
        onPrev={onPrev} onNext={onNext} onSkip={onSkip} isLast={isLast} hideIcon/>
    </div>
  );
}

// Corps commun (titre + puces + progression + boutons), réutilisé pour la
// carte centrée (intro/outro) et la carte flottante (étapes avec halo).
function TourCardBody({ current, Icon, step, steps, onPrev, onNext, onSkip, isLast, onClose, hideIcon }) {
  return (
    <div style={{ textAlign: 'left', position: 'relative' }}>
      {onClose && (
        <button onClick={onClose} title="Fermer"
          style={{ position: 'absolute', top: -6, right: -6, background: 'none', border: 'none',
            cursor: 'pointer', color: 'var(--text-3)' }}>
          <X size={16}/>
        </button>
      )}
      {!hideIcon && (
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${current.color}22`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
          <Icon size={20} color={current.color}/>
        </div>
      )}
      <h3 style={{ margin: '0 0 10px', fontSize: hideIcon ? 18 : 15, textAlign: hideIcon ? 'center' : 'left' }}>
        {current.title}
      </h3>
      <ul style={{ listStyle: 'none', margin: '0 0 14px', padding: 0, display: 'flex',
        flexDirection: 'column', gap: 8 }}>
        {current.bullets.map((b, i) => (
          <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
            color: 'var(--text-2)', fontSize: 12.5, lineHeight: 1.5 }}>
            <span style={{ flexShrink: 0, marginTop: 5, width: 5, height: 5, borderRadius: '50%',
              background: current.color }}/>
            {b}
          </li>
        ))}
      </ul>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8,
        textAlign: hideIcon ? 'center' : 'left' }}>
        {step + 1} / {steps.length}
      </div>
      <div style={{ display: 'flex', justifyContent: hideIcon ? 'center' : 'flex-start',
        gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {steps.map((_, i) => (
          <span key={i} style={{ width: i === step ? 16 : 6, height: 6, borderRadius: 99,
            transition: 'all 0.15s', background: i === step ? 'var(--accent)' : 'var(--border)' }}/>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {step > 0 ? (
          <button className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: 'center' }}
            onClick={onPrev}>
            <ChevronLeft size={13}/> Précédent
          </button>
        ) : (
          <button className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: 'center' }}
            onClick={onSkip}>
            Passer
          </button>
        )}
        <button className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: 'center' }}
          onClick={onNext}>
          {isLast ? 'Terminer' : <>Suivant <ChevronRight size={13}/></>}
        </button>
      </div>
    </div>
  );
}
